import { useMutation } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useToaster } from '../../../../core/components/Toast.tsx';
import { DELETE_AGENT_KNOWLEDGE_FILE, UPDATE_AGENT, UPLOAD_AGENT_KNOWLEDGE_FILE } from '../../queries.ts';
import { transformAgentFormData } from '../../utils/transformAgentFormData.ts';
import { AGENT_FORM_DEFAULT_VALUES, AgentFormData } from '../config/ConfigSteps.tsx';

const getAgentFormValues = (agent: any) => {
    const knowledgeFiles = (agent?.knowledgeFiles || []).filter((file) => file.sourceType !== 'url');

    return {
        name: agent?.name || '',
        description: agent?.description || '',
        instructions: agent?.instructions || [],
        erpToolProvider: agent?.erpToolProvider || null,
        masterDataToolProvider: agent?.masterDataToolProvider || null,
        sharepointToolProvider: agent?.sharepointToolProvider || null,
        sftpKnowledgeSource: agent?.sftpKnowledgeSource || null,
        knowledgeUrls: agent?.knowledgeUrls || [],
        orderHistoryToolProvider: agent?.orderHistoryToolProvider || null,
        defaultChannels: agent?.defaultChannels
            ? {
                  orderChannelId: agent.defaultChannels.orderChannelId || '',
                  orderConfirmationChannelId: agent.defaultChannels.orderConfirmationChannelId || '',
                  rfqChannelId: agent.defaultChannels.rfqChannelId || '',
                  invoiceChannelId: agent.defaultChannels.invoiceChannelId || '',
                  deliveryNoteChannelId: agent.defaultChannels.deliveryNoteChannelId || '',
              }
            : null,
        attachmentsEnabled: agent?.attachmentsEnabled ?? false,
        skillNames: agent?.skills?.map((skill) => skill.skillKey) || [],
        triggerIds: agent?.smartInboxIntegrations?.map((trigger) => trigger.id) || [],
        knowledgeBaseIds: agent?.knowledgeBases?.map((kb) => kb.id) || [],
        files: knowledgeFiles,
        deliveryMethod: agent?.deliveryMethod || 'auto',
        outputInstructions: agent?.outputInstructions || [],
        teamId: agent?.team?.id || undefined,
    };
};

const isExistingFile = (file: File | any) => 'id' in file && 'fileUrl' in file;
const isNewFile = (file: File | any): file is File => file instanceof File;

export const useAssistantConfigForm = (agent: any, onSaved: () => Promise<unknown>) => {
    const { t } = useTranslation('assistants');
    const { publishToast } = useToaster();

    const form = useForm({ defaultValues: AGENT_FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const [originalFiles, setOriginalFiles] = useState<any[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isSavingRef = useRef(false);

    useEffect(() => {
        const values = getAgentFormValues(agent);
        setOriginalFiles(values.files);
        const isAfterOwnSave = isSavingRef.current;
        isSavingRef.current = false;
        form.reset(values, isAfterOwnSave ? undefined : { keepDirtyValues: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agent]);

    const [updateAgent] = useMutation(UPDATE_AGENT);
    const [uploadAgentKnowledgeFile] = useMutation(UPLOAD_AGENT_KNOWLEDGE_FILE);
    const [deleteAgentKnowledgeFile] = useMutation(DELETE_AGENT_KNOWLEDGE_FILE);

    const save = async (data: AgentFormData) => {
        setIsSubmitting(true);
        isSavingRef.current = true;
        const failed: { type: 'delete' | 'upload'; name: string; error: unknown }[] = [];

        try {
            const currentFiles = data.files || [];

            // removed rows are deleted (synced files can't be removed here), freshly dropped files uploaded
            const filesToDelete = originalFiles.filter(
                (origFile) =>
                    !origFile.isSynced && !currentFiles.some((f: any) => isExistingFile(f) && f.id === origFile.id)
            );
            const filesToUpload = currentFiles.filter(isNewFile);

            for (const file of filesToDelete) {
                try {
                    await deleteAgentKnowledgeFile({ variables: { id: file.id } });
                } catch (error) {
                    failed.push({ type: 'delete', name: file.fileName, error });
                }
            }

            for (const file of filesToUpload) {
                try {
                    await uploadAgentKnowledgeFile({ variables: { agentId: agent.id, file } });
                } catch (error) {
                    failed.push({ type: 'upload', name: file.name, error });
                }
            }

            await updateAgent({ variables: { id: agent.id, input: transformAgentFormData(data) } });
            await onSaved();

            if (failed.length === 0) {
                publishToast({ description: t('detail.save.success'), status: 'success' });
            } else {
                publishToast({
                    description: t('detail.save.partialSuccess', { count: failed.length }),
                    status: 'warning',
                });
            }
        } catch (error) {
            console.error(error);
            publishToast({ description: t('detail.save.error'), status: 'error' });
            // sync the state even on failure
            await onSaved();
        } finally {
            setIsSubmitting(false);
        }
    };

    return { form, isSubmitting, onSubmit: form.handleSubmit(save as any) };
};
